import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { parse, startOfWeek, format, getDay } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../teams/teams.css';
import DateJumpInput from './DateJumpInput';

const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

function formatDateInputValue(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildScrollToTime(dateLike) {
  const fallback = new Date();
  fallback.setHours(8, 0, 0, 0);
  if (!dateLike) return fallback;
  const candidate = new Date(dateLike);
  if (Number.isNaN(candidate.getTime())) return fallback;
  candidate.setSeconds(0, 0);
  return candidate;
}

function pickEventField(event, details, key, fallback = null) {
  if (event?.[key] != null) return event[key];
  if (details?.[key] != null) return details[key];
  return fallback;
}

export default function StandardCalendarView({
  title,
  events,
  onSelectEvent,
  selectable = false,
  onSelectSlot,
  toolbarStart = null,
}) {
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');
  const [scrollToTime, setScrollToTime] = useState(() => buildScrollToTime(null));
  const [calendarHeight, setCalendarHeight] = useState(
    () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 420 : 560)
  );
  const [isMobile, setIsMobile] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < 768 : false)
  );
  const calendarShellRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const compactActionStyle = isMobile
    ? {
        padding: '0.45rem 0.7rem',
        fontSize: '0.9rem',
        lineHeight: 1.2,
      }
    : undefined;

  const recalcCalendarHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const top = calendarShellRef.current?.getBoundingClientRect().top ?? 0;
    const available = Math.floor(window.innerHeight - top - 12);
    const minHeight = isMobile ? 260 : 280;
    const maxHeight = Math.floor(window.innerHeight * (isMobile ? 0.86 : 0.85));
    let nextHeight = Math.min(available, maxHeight);
    if (available >= minHeight) {
      nextHeight = Math.max(nextHeight, minHeight);
    }
    setCalendarHeight(nextHeight);
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(recalcCalendarHeight);
    window.addEventListener('resize', recalcCalendarHeight);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', recalcCalendarHeight);
    };
  }, [recalcCalendarHeight, calendarView]);

  const monthEventCountByDate = useMemo(() => {
    const counts = {};
    (events || []).forEach((event) => {
      const key = formatDateInputValue(event.start);
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [events]);
  const getFirstEventStartForDate = useCallback((targetDate) => {
    const targetKey = formatDateInputValue(targetDate);
    if (!targetKey) return null;

    let earliest = null;
    (events || []).forEach((event) => {
      if (formatDateInputValue(event.start) !== targetKey) return;
      const start = new Date(event.start);
      if (Number.isNaN(start.getTime())) return;
      if (!earliest || start < earliest) earliest = start;
    });

    return earliest;
  }, [events]);
  const getSingleAgendaEventForDate = useCallback((targetDate) => {
    const targetKey = formatDateInputValue(targetDate);
    if (!targetKey) return null;
    const matches = (events || []).filter((event) => formatDateInputValue(event.start) === targetKey);
    return matches.length === 1 ? matches[0] : null;
  }, [events]);

  const eventPropGetter = useMemo(() => {
    return (event) => {
      const details = event?.details || {};
      const attendanceMode = pickEventField(event, details, 'attendanceMode', '');
      const mode = pickEventField(event, details, 'mode', '');
      const myRsvp = pickEventField(event, details, '_myRsvp', details?.rsvpStatus ?? null);
      const ticketStatus = pickEventField(event, details, 'ticketStatus', null);
      const isMonthView = calendarView === 'month';
      const isCompactMonth = isMonthView && isMobile;
      const style = {
        borderRadius: isMonthView ? (isCompactMonth ? 6 : 8) : 6,
        borderWidth: 1,
        borderStyle: 'solid',
        padding: isMonthView ? (isCompactMonth ? 2 : 6) : 4,
        '--evtTitle': '#0f172a',
      };

      if (attendanceMode === 'ticketed') {
        if (ticketStatus === 'paid') {
          style.backgroundColor = '#D1FAE5';
          style.borderColor = '#34D399';
          style['--evtTitle'] = '#065F46';
        } else {
          style.backgroundColor = '#EDE9FE';
          style.borderColor = '#C4B5FD';
          style['--evtTitle'] = '#3B0764';
        }
      } else if (attendanceMode === 'rsvp') {
        if (myRsvp === 'yes') {
          style.backgroundColor = '#D1FAE5';
          style.borderColor = '#34D399';
          style['--evtTitle'] = '#065F46';
        } else if (myRsvp === 'no') {
          style.backgroundColor = '#E5E7EB';
          style.borderColor = '#9CA3AF';
          style['--evtTitle'] = '#111827';
        } else {
          style.backgroundColor = '#BFDBFE';
          style.borderColor = '#60A5FA';
          style['--evtTitle'] = '#1E3A8A';
        }
      } else if (mode === 'online') {
        style.backgroundColor = '#ECFEFF';
        style.borderColor = '#67E8F9';
        style['--evtTitle'] = '#155E75';
      } else {
        style.backgroundColor = '#F1F5F9';
        style.borderColor = '#CBD5E1';
        style['--evtTitle'] = '#0F172A';
      }

      return { style };
    };
  }, [calendarView, isMobile]);

  const CalendarEvent = ({ event }) => {
    const details = event?.details || {};
    const attendanceMode = pickEventField(event, details, 'attendanceMode', '');
    const mode = pickEventField(event, details, 'mode', '');
    const needsResponse = pickEventField(event, details, '_needsResponse', false);
    const myRsvp = pickEventField(event, details, '_myRsvp', details?.rsvpStatus ?? null);
    const ticketStatus = pickEventField(event, details, 'ticketStatus', null);
    const pills = [];
    const startTime = event.start
      ? new Date(event.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : null;

    if (mode === 'inPerson') {
      pills.push({ label: 'In person', bg: '#e0e7ff', color: '#1e3a8a', border: '#c7d2fe' });
    } else if (mode === 'online' || details.onlineUrl || event.onlineUrl) {
      pills.push({ label: 'Online', bg: '#cffafe', color: '#0e7490', border: '#06b6d4' });
    }

    if (attendanceMode === 'ticketed') {
      pills.push({ label: 'Ticketed', bg: '#ede9fe', color: '#581c87', border: '#c4b5fd' });
      if (ticketStatus === 'paid') {
        pills.push({ label: 'Booked', bg: '#dcfce7', color: '#166534', border: '#86efac' });
      }
    } else if (attendanceMode === 'rsvp') {
      pills.push({ label: 'RSVP', bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' });
      if (needsResponse && myRsvp == null) {
        pills.push({ label: 'Respond', bg: '#ffedd5', color: '#9a3412', border: '#fdba74' });
      }
      if (myRsvp === 'yes') {
        pills.push({ label: 'Going', bg: '#dcfce7', color: '#166534', border: '#86efac' });
      } else if (myRsvp === 'no') {
        pills.push({ label: 'Not going', bg: '#e5e7eb', color: '#111827', border: '#9ca3af' });
      }
    }

    return (
      <div style={{ display: 'grid', gap: 2 }}>
        {startTime && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#0f172a',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              padding: '1px 5px',
              width: 'fit-content',
              lineHeight: 1.2,
            }}
          >
            {startTime}
          </div>
        )}
        <div style={{ fontWeight: 700, lineHeight: 1.1, fontSize: 12, color: 'var(--evtTitle, #0f172a)' }}>
          {event.title}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {pills.map((p, i) => (
            <span
              key={i}
              style={{
                fontSize: 10,
                padding: '1px 5px',
                borderRadius: 999,
                background: p.bg,
                color: p.color,
                border: `1px solid ${p.border}`,
                whiteSpace: 'nowrap',
              }}
            >
              {p.label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const CalendarEventCompact = ({ event }) => {
    const startTime = event.start
      ? new Date(event.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : null;

    return (
      <div
        style={{
          fontWeight: 700,
          lineHeight: 1.2,
          fontSize: 12,
          color: 'var(--evtTitle, #0f172a)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {startTime ? `${startTime} ` : ''}{event.title}
      </div>
    );
  };

  const AgendaEvent = ({ event, title }) => (
    <div className="team-calendar-agenda-event">
      {title || event.title}
    </div>
  );

  const AgendaDate = ({ day, label }) => {
    const event = getSingleAgendaEventForDate(day);
    if (!event || !onSelectEvent) return <span>{label}</span>;
    return (
      <button
        type="button"
        className="team-calendar-agenda-cell-button"
        onClick={(e) => {
          e.stopPropagation();
          onSelectEvent(event, e);
        }}
      >
        {label}
      </button>
    );
  };

  const AgendaTime = ({ event, label }) => {
    if (!onSelectEvent) return <span>{label}</span>;
    return (
      <button
        type="button"
        className="team-calendar-agenda-cell-button"
        onClick={(e) => {
          e.stopPropagation();
          onSelectEvent(event, e);
        }}
      >
        {label}
      </button>
    );
  };

  const MonthDateHeader = ({ date, label }) => {
    const dayKey = formatDateInputValue(date);
    const eventCount = monthEventCountByDate[dayKey] || 0;
    const hasMultipleEvents = eventCount > 1;
    const shouldOpenDayFromHeader = calendarView === 'month' && (isMobile || hasMultipleEvents);
    const openDayFromHeader = (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      const nextDate = new Date(date);
      if (Number.isNaN(nextDate.getTime())) return;
      setScrollToTime(buildScrollToTime(getFirstEventStartForDate(nextDate)));
      setCalendarDate(nextDate);
      setCalendarView('day');
    };

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}
        role={shouldOpenDayFromHeader ? 'button' : undefined}
        tabIndex={shouldOpenDayFromHeader ? 0 : undefined}
        onMouseDown={shouldOpenDayFromHeader ? (e) => {
          e.preventDefault();
          e.stopPropagation();
        } : undefined}
        onClick={shouldOpenDayFromHeader ? openDayFromHeader : undefined}
        onKeyDown={shouldOpenDayFromHeader ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDayFromHeader();
          }
        } : undefined}
      >
        <span>{label}</span>
        {hasMultipleEvents && (
          <span
            title={`${eventCount} events on this day.`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: '#0f172a',
              background: '#e2e8f0',
              border: '1px solid #cbd5e1',
              lineHeight: 1,
            }}
          >
            {eventCount > 9 ? '9+' : eventCount}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="calendar-page">
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {toolbarStart}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <label htmlFor="calendar-date-jump" style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
            Jump to date
          </label>
          <DateJumpInput
            id="calendar-date-jump"
            date={calendarDate}
            onDateChange={setCalendarDate}
          />
          <button
            className="button button-secondary"
            onClick={() => setCalendarDate(new Date())}
            style={compactActionStyle}
          >
            Today
          </button>
        </div>
      </div>

      <div ref={calendarShellRef}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: calendarHeight }}
          defaultView="month"
          view={calendarView}
          onView={(nextView) => setCalendarView(nextView)}
          date={calendarDate}
          onNavigate={(nextDate) => setCalendarDate(nextDate)}
          scrollToTime={scrollToTime}
          popup
          showAllEvents={false}
          allDayMaxRows={calendarView === 'month' && isMobile ? 1 : 2}
          dayLayoutAlgorithm="no-overlap"
          selectable={selectable}
          eventPropGetter={eventPropGetter}
          components={{
            event: calendarView === 'month' && !isMobile ? CalendarEvent : CalendarEventCompact,
            agenda: { date: AgendaDate, event: AgendaEvent, time: AgendaTime },
            month: { dateHeader: MonthDateHeader },
          }}
          onSelectEvent={onSelectEvent}
          onSelectSlot={(slot) => {
            if (calendarView === 'month' && isMobile) {
              const nextDate = new Date(slot.start);
              if (Number.isNaN(nextDate.getTime())) return;
              setCalendarDate(nextDate);
              setCalendarView('day');
              return;
            }
            if (onSelectSlot) onSelectSlot(slot);
          }}
        />
      </div>
    </div>
  );
}
